/**
 * TreatmentRequestChangeSection - Section for orphaned treatment requests.
 *
 * Shows treatment requests not associated with a provider in the current view.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { TreatmentRequestChangeCard } from './TreatmentRequestChangeCard';

interface TreatmentRequestChangeSectionProps {
  treatmentRequests: EntityChangeSchema[];
  matterId: string;
}

export const TreatmentRequestChangeSection: React.FC<
  TreatmentRequestChangeSectionProps
> = ({ treatmentRequests, matterId }) => {
  const sorted = useMemo(
    () =>
      [...treatmentRequests].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [treatmentRequests],
  );

  if (treatmentRequests.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          Treatment Requests
        </h3>
        <Badge color="zinc" className="text-xs">
          {treatmentRequests.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((request) => (
          <TreatmentRequestChangeCard key={request.id} entity={request} matterId={matterId} />
        ))}
      </div>
    </div>
  );
};
